import React, { useState, useCallback } from 'react';
import { ApiService } from '../services/api';
import { Application } from '../types/models';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';

interface EditableCommentProps {
  application: Application;
  onCommentUpdated: (updatedApp: Application) => void;
}

const EditableComment: React.FC<EditableCommentProps> = ({ application, onCommentUpdated }) => {
  const [comment, setComment] = useState(application.comment || '');
  const [isEditingComment, setIsEditingComment] = useState(false);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  }, []);

  const handleCommentBlur = useCallback(async () => {
    setIsEditingComment(false);
    if (comment !== application.comment) {
      try {
        // Сразу обновляем локальное состояние
        onCommentUpdated({ ...application, comment });
        
        // Отправляем на бэкенд только комментарий
        await ApiService.updateApplicationComment(application.id, comment);
      } catch (error) {
        console.error("Failed to update comment:", error);
        // Откатываем изменение, если произошла ошибка на бэкенде
        setComment(application.comment || '');
        onCommentUpdated(application);
      }
    }
  }, [comment, application, onCommentUpdated]);

  const handleCommentKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLTextAreaElement).blur(); // Вызываем blur для сохранения
    }
  }, []);

  return (
    <div className="flex items-start">
      <ChatBubbleLeftEllipsisIcon className="h-4 w-4 mr-2 text-slate-400 mt-1" />
      {isEditingComment ? (
        <textarea
          value={comment}
          onChange={handleCommentChange}
          onBlur={handleCommentBlur}
          onKeyDown={handleCommentKeyDown}
          className="flex-grow bg-slate-700 text-slate-200 text-sm rounded p-2 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y min-h-[40px]"
          autoFocus
        />
      ) : (
        <p
          className="flex-grow text-slate-200 text-sm cursor-pointer line-clamp-2 group-hover:line-clamp-none transition-all break-words"
          onClick={() => setIsEditingComment(true)}
        >
          {comment || "Добавить комментарий..."}
        </p>
      )}
    </div>
  );
};

export default EditableComment;